import type { AccountStatistics, SormRow } from '../account-query-sorm-types'

import { Temporal } from 'temporal-polyfill'

import { MOMENT_STATUS_BY_SYNC_STATUS } from '../constants'
import {
  readSormNullableString,
  readSormNumber,
  readSormString,
} from './sorm-row-readers'

export function mapMomentStatus(row: SormRow): string | null {
  const syncStatus = readSormNullableString(row.sync_status)
  if (syncStatus == null) {
    return null
  }
  return MOMENT_STATUS_BY_SYNC_STATUS[syncStatus] ?? syncStatus
}

export function mapAccountExtra(row: SormRow): Record<string, string> | null {
  const extra: Record<string, string> = {}
  const notes = row.notes
  const firstNote = Array.isArray(notes) ? notes[0] : undefined
  if (typeof firstNote === 'string' && firstNote !== '') {
    extra['_moment.notes'] = firstNote
  }
  const moneyManager = readSormString(row.money_manager)
  if (moneyManager !== '') {
    extra.money_manager = moneyManager
  }
  return Object.keys(extra).length > 0 ? extra : null
}

export function mapCustodian(
  row: SormRow,
): { id: string; name: string } | null {
  const id = readSormString(row.custodian_id)
  if (id === '') {
    return null
  }
  return { id, name: readSormString(row.custodian_name) || id }
}

export function mapCashLots(
  row: SormRow,
): Array<{ settled_open_quantity: number }> | null {
  const cash = readSormNumber(row.cash_market_value)
  return cash === 0 ? null : [{ settled_open_quantity: cash }]
}

export function mapAccountStatistics(row: SormRow): AccountStatistics {
  const totalMarketValue = readSormNumber(row.total_market_value)
  const cashMarketValue = readSormNumber(row.cash_market_value)
  let percentUninvestedCash = readSormNumber(row.percent_uninvested_cash)
  if (percentUninvestedCash === 0 && totalMarketValue > 0) {
    percentUninvestedCash = (cashMarketValue / totalMarketValue) * 100
  }
  return {
    timestamp: Temporal.Now.instant().toString(),
    total_market_value: totalMarketValue,
    cash_market_value: cashMarketValue,
    tracked_fixed_income_market_value: readSormNumber(
      row.tracked_fixed_income_market_value,
    ),
    percent_uninvested_cash: percentUninvestedCash,
    average_ytw: readSormNumber(row.average_ytw),
    average_book_ytw: null,
    average_maturity_in_years: readSormNumber(row.average_maturity_in_years),
    average_moodys_rating: readSormNullableString(row.average_moodys_rating),
    average_sp_rating: readSormString(row.average_sp_rating),
    average_coupon: readSormNumber(row.average_coupon),
    average_duration: readSormNumber(row.average_duration),
  }
}
